import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useActivity, useApiConfig, useCandles, useMarket, useOptionChain, useStats, type ChainSeries, type Market } from "../lib/api";
import { HOLDER_SHARE, PONS_URL, VAULT_SHARE } from "../lib/config";
import { fmtEth, fmtMcap, fmtNum, fmtPct, fmtUsd, shortAddr, timeLeft, wei } from "../lib/format";
import { useMode } from "../lib/demo";
import { PayoffChart } from "../components/PayoffChart";
import { PriceChart, type ChartLine } from "../components/PriceChart";
import { Countdown, nextPayoutTs } from "../components/Countdown";
import { CaChip } from "../components/CaChip";
import "./home.css";

export function Home() {
  const { data: cfg } = useApiConfig();
  const { data: m } = useMarket();
  const { data: stats } = useStats();
  const mode = useMode();
  const token = cfg?.contracts.token;
  const graduated = m?.phase === 2;

  return (
    <>
      <section className="hero wrap">
        <div className="hero-copy">
          <span className="hero-eyebrow mono">
            <span className="dot live" /> {mode === "demo" ? "Launching on Robinhood Chain" : "Live on Robinhood Chain"}
          </span>
          <h1 className="hero-title">The coin with its own options market.</h1>
          <p className="hero-sub">
            Calls and puts on $STRIKE, paid in ETH. Every option is fully collateralized and settled on-chain, and holders earn ETH
            from every trade.
          </p>
          <div className="hero-cta">
            <Link to="/trade" className="btn btn-primary btn-lg">
              Launch app <Arrow />
            </Link>
            {token && mode !== "demo" ? (
              <a className="btn btn-lg btn-glass" href={`${PONS_URL}/${token}`} target="_blank" rel="noreferrer">
                Buy $STRIKE
              </a>
            ) : (
              <Link to="/docs" className="btn btn-lg btn-glass">
                Read the docs
              </Link>
            )}
          </div>
          <CaChip />
        </div>
        <StrikeBoard m={m} />
      </section>

      <section className="wrap">
        <div className="stats-band">
          <Stat label="Market cap" value={m?.launched ? fmtUsd(m.marketCapUsd) : "—"}>
            {m?.launched ? <span className={m.change24hPct >= 0 ? "call" : "put"}>{fmtPct(m.change24hPct)} today</span> : "pre-launch"}
          </Stat>
          <Stat label="Paid to holders" value={stats ? fmtEth(wei(stats.paidToHolders) + wei(stats.allocatedUnclaimed)) : "—"}>
            in ETH, since launch
          </Stat>
          <Stat label="Options traded" value={stats ? fmtNum(wei(stats.optionsTraded)) : "—"}>
            {stats ? `${fmtEth(wei(stats.premiumsWeth))} in premiums` : "calls and puts"}
          </Stat>
          <Stat label="Next payout" value={<Countdown to={nextPayoutTs()} />}>
            {stats ? `${fmtEth(wei(stats.nextPayoutPot))} in the pot` : "00:00 and 12:00 UTC"}
          </Stat>
        </div>
        {m?.launched && !graduated && m.graduation && (
          <div className="grad panel">
            <div className="grad-top">
              <span>
                <strong>Options open at graduation.</strong> <span className="mute">The pons curve fills at {m.graduation.thresholdEth} ETH.</span>
              </span>
              <span className="num">{Math.round(m.graduation.progress * 100)}%</span>
            </div>
            <div className="grad-bar">
              <span style={{ width: `${Math.max(2, m.graduation.progress * 100)}%` }} />
            </div>
          </div>
        )}
      </section>

      <Tape />

      <section className="wrap sec" id="ways">
        <SectionHead eyebrow="One coin, three ways" title="Hold it. Call it. Put it." sub="Whatever you think $STRIKE does next, there is a way to be paid for it." />
        <div className="ways">
          <article className="way">
            <div className="way-vis">
              <HoldVisual />
            </div>
            <div className="way-body">
              <span className="way-tag">Hold it</span>
              <h3>Get paid in ETH.</h3>
              <p>
                {HOLDER_SHARE}% of the creator tax and of every options fee is paid to holders in ETH, twice a day. The rest keeps the
                options market liquid.
              </p>
              <Link to="/rewards" className="way-link">
                Holder rewards <Arrow />
              </Link>
            </div>
          </article>
          <article className="way">
            <div className="way-vis">
              <PayoffVisual isCall />
            </div>
            <div className="way-body">
              <span className="way-tag call">Call it</span>
              <h3>Bet it pumps.</h3>
              <p>Pay a small premium for the right to buy at a set market cap. If $STRIKE runs past it, cash out the difference in ETH.</p>
              <Link to="/trade" className="way-link call">
                Buy a call <Arrow />
              </Link>
            </div>
          </article>
          <article className="way">
            <div className="way-vis">
              <PayoffVisual isCall={false} />
            </div>
            <div className="way-body">
              <span className="way-tag put">Put it</span>
              <h3>Bet it dumps.</h3>
              <p>The first memecoin you can short. Buy a put to profit if the price falls, or to protect the bag you already hold.</p>
              <Link to="/trade" className="way-link put">
                Buy a put <Arrow />
              </Link>
            </div>
          </article>
        </div>
      </section>

      <section className="wrap sec" id="how">
        <SectionHead eyebrow="How it works" title="Three steps. Under a minute." />
        <div className="steps3">
          <Step n={1} title="Pick a strike" text="Choose the market cap you think $STRIKE will pass, and when: tomorrow, this Friday or next month.">
            <div className="snip snip-chain">
              <div className="snip-row">
                <span className="call num">0.0412</span>
                <span className="num">$2.5M</span>
                <span className="put num">0.0021</span>
              </div>
              <div className="snip-row on">
                <span className="call num">0.0184</span>
                <span className="num">$3.0M</span>
                <span className="faint">—</span>
              </div>
              <div className="snip-row">
                <span className="call num">0.0063</span>
                <span className="num">$4.0M</span>
                <span className="faint">—</span>
              </div>
            </div>
          </Step>
          <Step n={2} title="Pay the premium in ETH" text="One transaction. The price is all you can lose: no leverage, no liquidation, no margin calls.">
            <div className="snip">
              <div className="kv">
                <span>You pay</span>
                <span>0.0184 ETH</span>
              </div>
              <div className="kv">
                <span>Breakeven</span>
                <span>$3.05M</span>
              </div>
              <div className="snip-btn">Buy 1M calls</div>
            </div>
          </Step>
          <Step n={3} title="Cash out in one click" text="In the money? The contract exercises and sells for you in the same transaction. Profit lands in ETH.">
            <div className="snip snip-toast">
              <span className="snip-ok" />
              <div>
                <div>Cashed out 1M calls</div>
                <div className="call num">+0.3120 ETH</div>
              </div>
            </div>
          </Step>
        </div>
      </section>

      <Explainer priceEth={m?.priceEth} ethUsd={m?.ethUsd} />

      <section className="wrap sec">
        <SectionHead eyebrow="Where the money goes" title="Fees pay holders and fund the market." />
        <div className="flow">
          <div className="flow-col">
            <div className="flow-node">
              <span className="eyebrow">Creator tax</span>
              <p>On every $STRIKE buy and sell, in ETH.</p>
            </div>
            <div className="flow-node">
              <span className="eyebrow">Options fee</span>
              <p>10% of every call and put premium.</p>
            </div>
          </div>
          <FlowWire />
          <div className="flow-node flow-mid">
            <span className="eyebrow">Treasury</span>
            <p>Collects every fee in ETH and splits it twice a day.</p>
          </div>
          <FlowWire split />
          <div className="flow-col">
            <div className="flow-node flow-end">
              <span className="eyebrow call">Holders · {HOLDER_SHARE}%</span>
              <p>Paid in ETH every 12 hours by average balance.</p>
            </div>
            <div className="flow-node">
              <span className="eyebrow">Options vault · {VAULT_SHARE}%</span>
              <p>Sells calls and puts, so there is always something to buy.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap sec">
        <SectionHead eyebrow="Security" title="Fully backed. Nothing to manipulate." />
        <div className="safe">
          <Safe icon="lock" title="Collateral first" text="Every call is backed by $STRIKE and every put by ETH, locked in an options contract that cannot be upgraded." />
          <Safe icon="scale" title="No price referee" text="No oracle decides who wins. Cashing out is a real trade, so pumping the pool to win costs more than it pays." />
          <Safe icon="eye" title="Open books" text="Quotes are checked on-chain against the live price, and every holder payout is published with its proofs." />
        </div>
      </section>

      <Faq />

    </>
  );
}

function Arrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="arrow">
      <path d="M3 8h9M8.5 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <div className="sec-head">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
    </div>
  );
}

function Stat({ label, value, children }: { label: string; value: ReactNode; children: ReactNode }) {
  return (
    <div className="sb-item">
      <span className="eyebrow">{label}</span>
      <span className="sb-v num">{value}</span>
      <span className="sb-s mono">{children}</span>
    </div>
  );
}

/**
 * The hero's live board: $STRIKE's market cap with the strikes you can trade drawn on it,
 * calls above the price and puts below, for the expiry closest to a week out.
 */
function StrikeBoard({ m }: { m?: Market }) {
  const { data: chain } = useOptionChain();
  const { data: candles } = useCandles(900);
  const spot = m?.priceEth ?? 0;
  const eu = m?.ethUsd ?? 0;
  const { picks, expiry } = useMemo(() => featuredStrikes(chain?.series ?? [], spot), [chain, spot]);
  const lines: ChartLine[] = picks.map((s) => ({
    priceEth: s.strikeEth,
    color: s.isCall ? "#c6ff3d" : "#ff4d6d",
    title: s.isCall ? "CALL" : "PUT",
  }));

  return (
    <div className="board">
      <div className="board-top">
        <div className="board-id">
          <img src="/mark.svg" alt="" width="32" height="32" />
          <div>
            <div className="mono mute board-lbl">$STRIKE market cap</div>
            <div className="board-cap">
              <span className="num">{m?.launched ? fmtUsd(m.marketCapUsd) : "—"}</span>
              {m?.launched && (
                <span className={`board-chg num ${m.change24hPct >= 0 ? "up" : "down"}`}>{fmtPct(m.change24hPct)}</span>
              )}
            </div>
          </div>
        </div>
        <span className="board-live mono">
          <span className="dot live" /> Live
        </span>
      </div>
      <div className="board-chart">
        {candles && candles.length > 0 ? (
          <PriceChart candles={candles} ethUsd={eu} height={300} variant="area" lines={lines} />
        ) : (
          <div className="board-empty mono">The chart starts at launch</div>
        )}
      </div>
      <div className="board-foot">
        <span className="mono mute">
          {expiry ? `Strikes expiring ${fmtShortDate(expiry)} · ${timeLeft(expiry)} left · prices per 1M options` : "Options open at graduation"}
        </span>
        <div className="board-picks">
          {picks.map((s) => (
            <Link key={s.id} to={`/trade?s=${s.id}`} className={`board-pick ${s.isCall ? "call" : "put"}`}>
              <span className="bp-top">
                <span className="mono">{s.isCall ? "Call" : "Put"}</span>
                <span className="num">{fmtMcap(s.strikeEth, eu)}</span>
              </span>
              <span className="bp-price num">{fmtEth(s.askEth * 1e6, 3)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Two calls above the price and two puts below it, spread out, for the expiry closest to a week out. */
function featuredStrikes(series: ChainSeries[], spot: number): { picks: ChainSeries[]; expiry?: number } {
  if (!series.length || spot <= 0) return { picks: [] };
  const week = Date.now() / 1000 + 7 * 86_400;
  const expiry = [...new Set(series.map((s) => s.expiry))].sort((a, b) => Math.abs(a - week) - Math.abs(b - week))[0];
  const here = series.filter((s) => s.expiry === expiry);
  const nearest = (isCall: boolean, m: number, not?: ChainSeries) =>
    here
      .filter((s) => s.isCall === isCall && s !== not && (isCall ? s.strikeEth > spot : s.strikeEth < spot))
      .sort((a, b) => Math.abs(a.strikeEth / spot - m) - Math.abs(b.strikeEth / spot - m))[0];
  const c1 = nearest(true, 1.2);
  const c2 = nearest(true, 1.5, c1);
  const p1 = nearest(false, 0.85);
  const p2 = nearest(false, 0.7, p1);
  const picks = [c2, c1, p1, p2].filter((x): x is ChainSeries => !!x);
  return { picks: [...new Map(picks.map((x) => [x.id, x])).values()], expiry };
}

const fmtShortDate = (ts: number) => new Date(ts * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** Recent fills, cash-outs and claims, scrolling. */
function Tape() {
  const { data: act } = useActivity();
  const { data: chain } = useOptionChain();
  const { data: m } = useMarket();
  const eu = m?.ethUsd ?? 0;
  const byId = useMemo(() => new Map((chain?.series ?? []).map((s) => [s.id, s])), [chain]);
  const now = Date.now() / 1000;
  const items = (act ?? [])
    .map((a) => {
      const ago = <span className="faint">{fmtAgo(now - a.ts)}</span>;
      if (a.name === "Filled") {
        const s = byId.get(a.args.seriesId);
        const kind = s ? (s.isCall ? "calls" : "puts") : "options";
        return (
          <>
            <span className="mono">{shortAddr(a.args.taker)}</span> bought{" "}
            <span className={s?.isCall === false ? "put" : "call"}>
              {fmtNum(wei(a.args.amount))} {kind}
            </span>
            {s && <> at {fmtMcap(s.strikeEth, eu)}</>} {ago}
          </>
        );
      }
      if (a.name === "CashedOut") {
        return (
          <>
            <span className="mono">{shortAddr(a.args.holder)}</span> cashed out <span className="call">+{fmtEth(wei(a.args.profit))}</span> {ago}
          </>
        );
      }
      if (a.name === "Claimed") {
        return (
          <>
            <span className="mono">{shortAddr(a.args.account)}</span> claimed {fmtEth(wei(a.args.amount))} in rewards {ago}
          </>
        );
      }
      return null;
    })
    .filter(Boolean)
    .slice(0, 14);
  if (items.length < 3) return null;
  const row = items.map((node, i) => (
    <span className="tape-item" key={i}>
      {node}
    </span>
  ));
  return (
    <div className="tape" aria-label="Recent activity">
      <div className="tape-track">
        {row}
        <span aria-hidden="true" style={{ display: "contents" }}>
          {row}
        </span>
      </div>
    </div>
  );
}

function fmtAgo(s: number): string {
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/** A call or put payoff drawn large: flat until the strike, then the profit side lights up. */
function PayoffVisual({ isCall }: { isCall: boolean }) {
  const c = isCall ? "var(--call)" : "var(--put)";
  const id = isCall ? "pv-c" : "pv-p";
  const d = isCall ? "M20 150 H170 L300 30" : "M20 30 L150 150 H300";
  const area = isCall ? "M170 150 L300 30 V150 Z" : "M20 30 L150 150 H20 Z";
  return (
    <svg viewBox="0 0 320 180" className="pv" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c} stopOpacity=".28" />
          <stop offset="1" stopColor={c} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[40, 80, 120, 160].map((y) => (
        <line key={y} x1="0" x2="320" y1={y} y2={y} className="pv-grid" />
      ))}
      <path d={area} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={c} strokeWidth="3.5" strokeLinejoin="round" className="pv-line" />
      <circle cx={isCall ? 170 : 150} cy="150" r="5" fill="var(--ink)" stroke={c} strokeWidth="2.5" />
      <text x={isCall ? 170 : 150} y="172" textAnchor="middle" className="pv-lbl">
        strike
      </text>
    </svg>
  );
}

/** A ring counting down to the next 12-hour payout, with this epoch's pot. */
function HoldVisual() {
  const { data: stats } = useStats();
  const next = nextPayoutTs();
  const left = Math.max(0, next - Date.now() / 1000);
  const frac = 1 - left / 43_200;
  const R = 58;
  const C = 2 * Math.PI * R;
  return (
    <div className="hv">
      <svg viewBox="0 0 150 150" className="hv-ring" aria-hidden="true">
        <circle cx="75" cy="75" r={R} className="hv-track" />
        <circle cx="75" cy="75" r={R} className="hv-fill" strokeDasharray={`${C * frac} ${C}`} transform="rotate(-90 75 75)" />
      </svg>
      <div className="hv-text">
        <span className="num hv-pot">{stats ? fmtEth(wei(stats.nextPayoutPot)) : "ETH"}</span>
        <span className="mono faint">
          next payout <Countdown to={next} />
        </span>
      </div>
    </div>
  );
}

function Step({ n, title, text, children }: { n: number; title: string; text: string; children: ReactNode }) {
  return (
    <div className="step">
      <div className="step-vis">{children}</div>
      <span className="step-n mono">0{n}</span>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

const ICONS: Record<string, ReactNode> = {
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  scale: <path d="M12 4v16M6 20h12M4 8h16M7 8l-3 6a3 3 0 0 0 6 0zM17 8l-3 6a3 3 0 0 0 6 0z" />,
  eye: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
};

function Safe({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="safe-card">
      <svg viewBox="0 0 24 24" className="safe-icon" aria-hidden="true">
        {ICONS[icon]}
      </svg>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function FlowWire({ single = false, split = false }: { single?: boolean; split?: boolean }) {
  return (
    <svg className={`flow-wire ${single ? "single" : ""}`} viewBox="0 0 80 200" preserveAspectRatio="none" aria-hidden="true">
      {single ? (
        <path d="M0 100 H80" />
      ) : split ? (
        <>
          <path d="M0 100 C40 100 40 50 80 50" />
          <path d="M0 100 C40 100 40 150 80 150" />
        </>
      ) : (
        <>
          <path d="M0 50 C40 50 40 100 80 100" />
          <path d="M0 150 C40 150 40 100 80 100" />
        </>
      )}
    </svg>
  );
}

const FAQ: [string, string][] = [
  [
    "What is $STRIKE?",
    "A coin on Robinhood Chain, launched on pons, that comes with its own market for calls and puts on itself. Hold it to earn ETH, or trade options on where its price goes.",
  ],
  [
    "What are calls and puts?",
    "A call is the right to buy $STRIKE at a set market cap (the strike) until a set date. It gains value when the price goes up. A put is the right to sell at the strike and gains value when the price goes down. You pay a small price for that right, the premium.",
  ],
  [
    "How do I make money with an option?",
    "If your option ends up on the right side of its strike, press Cash out in Portfolio. The contract uses your option and sells the result in the pool in one transaction, and you receive the profit in ETH.",
  ],
  [
    "What is the most I can lose?",
    "Only the premium you paid. There is no leverage to liquidate and no margin call. If the price never reaches your strike, the option expires worthless.",
  ],
  ["How do holders get paid?", "Every 12 hours, 70% of the creator tax and of every options fee is split among holders by average balance, in ETH. Claim on the Rewards page. The other 30% funds the vault that sells options."],
  [
    "When do options open?",
    "When the pons bonding curve fills and $STRIKE graduates to its locked pool. From then on there are always daily, weekly and monthly expiries, each with a wide range of strikes, all expiring at 20:00 UTC.",
  ],
];

function Faq() {
  return (
    <section className="wrap sec faq">
      <div className="faq-side">
        <SectionHead eyebrow="FAQ" title="The short version." />
        <p className="mute">
          Everything else is in the <Link to="/docs" className="call">docs</Link>, in five minutes of reading.
        </p>
      </div>
      <div className="faq-list">
        {FAQ.map(([q, a], i) => (
          <details key={q} className="faq-item" open={i === 0}>
            <summary>
              {q}
              <span className="faq-plus" aria-hidden="true" />
            </summary>
            <p>{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/** Interactive: pick call or put, drag the market cap at expiry, see what the option pays. */
function Explainer({ priceEth, ethUsd }: { priceEth?: number; ethUsd?: number }) {
  const [isCall, setIsCall] = useState(true);
  const [move, setMove] = useState(2);
  const spot = priceEth && priceEth > 0 ? priceEth : 2e-8;
  const eu = ethUsd ?? 2600;
  const strike = spot * (isCall ? 1.25 : 0.8);
  const premium = spot * 0.02;
  const size = 1_000_000;
  const friday = spot * move;
  const pnlEth = ((isCall ? Math.max(0, friday - strike) : Math.max(0, strike - friday)) - premium) * size;
  const label = useMemo(() => (p: number) => fmtMcap(p, eu), [eu]);

  return (
    <section className="wrap sec">
      <SectionHead eyebrow="Try it" title="Pay a little. Win if you're right." />
      <div className="explain">
        <div className="explain-copy">
          <div className={`tabs ${isCall ? "call" : "put"}`}>
            <button className={isCall ? "on" : ""} onClick={() => (setIsCall(true), setMove(2))}>
              Call
            </button>
            <button className={!isCall ? "on" : ""} onClick={() => (setIsCall(false), setMove(0.5))}>
              Put
            </button>
          </div>
          <p className="explain-line">
            Buy a {isCall ? "call" : "put"} on 1M $STRIKE at <strong>{label(strike)}</strong> for <strong>{fmtEth(premium * size)}</strong>.
          </p>
          <label className="field">
            <span className="mute" style={{ fontSize: 14 }}>
              If $STRIKE is at{" "}
              <strong className="num" style={{ color: "var(--text)" }}>
                {label(friday)}
              </strong>{" "}
              at expiry…
            </span>
            <input
              type="range"
              min={0.2}
              max={3.5}
              step={0.01}
              value={move}
              onChange={(e) => setMove(Number(e.target.value))}
              className={`range ${isCall ? "" : "range-put"}`}
              aria-label="Market cap at expiry"
            />
          </label>
          <div className={`explain-result ${pnlEth > 0 ? (isCall ? "call" : "put") : "mute"}`}>
            <span className="eyebrow">You {pnlEth > 0 ? "make" : "lose"}</span>
            <span className="num explain-pnl">
              {pnlEth > 0 ? "+" : ""}
              {fmtEth(pnlEth)}
            </span>
            <span className="note">
              {pnlEth > 0 ? `${fmtNum(pnlEth / (premium * size) + 1)}x your premium, cashed out in one click.` : "At most the premium. No liquidation, no margin call."}
            </span>
          </div>
        </div>
        <div className="explain-chart">
          <PayoffChart isCall={isCall} strike={strike} premium={premium} spot={friday} height={280} labelFor={label} />
        </div>
      </div>
    </section>
  );
}
